// src/record/util.ts
var randomString = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};
var runIdleCallback = (cb) => {
  if ("requestIdleCallback" in window) {
    return window.requestIdleCallback(cb);
  } else {
    return setTimeout(cb, 100);
  }
};
var cancelIdleCallback = (id) => {
  if ("requestIdleCallback" in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
};

// src/record/consumerBuffer.ts
var ConsumerBuffer = class {
  constructor(engine) {
    this.consumeEvents = null;
    this.queue = [];
    this.failsInARow = 0;
    this.maxFailsInARow = 10;
    this.destroyed = false;
    this.pendingStepId = null;
    this.engine = engine;
  }
  onEvent(event) {
    if (this.destroyed) return;
    this.queue.push(event);
  }
  processQueue() {
    if (this.queue.length === 0) return;
    const batch = this.queue.slice();
    this.queue = [];
    if (this.consumeEvents != null) {
      this.consumeEvents(batch);
    }
    this.engine.notifyPlugins(batch);
  }
  step() {
    if (this.destroyed) return;
    this.pendingStepId = setTimeout(async () => {
      this.pendingStepId = null;
      await Promise.resolve();
      runIdleCallback(() => {
        this.consume();
      });
    }, 250);
  }
  start() {
    if (!this.engine.isRunning) return;
    this.step();
  }
  consume() {
    if (this.destroyed) return;
    if (!this.engine.isRunning) return;
    try {
      this.processQueue();
    } catch (e) {
      this.failsInARow++;
      if (this.failsInARow > this.maxFailsInARow) {
        this.engine.error("[axos] Too many errors in a row, resetting events");
        this.queue = [];
      }
      this.engine.error("[axos] Error processing event queue", e);
    }
    this.step();
  }
  destroy() {
    this.destroyed = true;
    if (this.pendingStepId != null) {
      clearTimeout(this.pendingStepId);
      this.pendingStepId = null;
    }
    this.queue = [];
    this.consumeEvents = null;
  }
};

// src/record/events.ts
var EngineEvents = class {
  constructor(engine) {
    this._listenerId = 0;
    this.listeners = {};
    this.engine = engine;
  }
  getNewListenerId() {
    if (this._listenerId >= 1e6) this._listenerId = 0;
    return this._listenerId++;
  }
  on(event, listener) {
    if (!this.listeners[event]) this.listeners[event] = {};
    const id = this.getNewListenerId();
    this.listeners[event][id] = listener;
    return () => {
      delete this.listeners[event][id];
    };
  }
  emit(event, payload) {
    if (!this.listeners[event]) return;
    Object.values(this.listeners[event]).forEach((listener) => {
      listener(payload);
    });
  }
  destroy() {
    this.listeners = {};
  }
};

// src/record/buffer.ts
var EventBuffer = class {
  constructor(processBatch) {
    this.processBatch = null;
    this.queue = [];
    this.scheduledId = null;
    this.held = false;
    this.processBatch = processBatch;
  }
  add(payload) {
    this.queue.push(payload);
    if (!this.held && this.scheduledId == null) this.schedule();
  }
  flush() {
    this.cancelScheduled();
    if (this.processBatch == null) return;
    if (this.queue.length === 0) return;
    const batch = this.queue;
    this.queue = [];
    this.processBatch(batch);
  }
  /** Pause automatic scheduling. Items still accumulate in the queue. */
  hold() {
    this.held = true;
    this.cancelScheduled();
  }
  /** Resume automatic scheduling and trigger a flush if items are queued. */
  release() {
    this.held = false;
    if (this.queue.length > 0) this.schedule();
  }
  /** Discard all queued items and reset hold state. */
  clear() {
    this.cancelScheduled();
    this.queue = [];
    this.held = false;
  }
  schedule() {
    this.scheduledId = runIdleCallback(() => {
      this.scheduledId = null;
      this.flush();
    });
  }
  cancelScheduled() {
    if (this.scheduledId == null) return;
    cancelIdleCallback(this.scheduledId);
    this.scheduledId = null;
  }
  destroy() {
    this.cancelScheduled();
    this.queue = [];
    this.processBatch = null;
  }
};

// src/record/extract.ts
var EMPTY_FROZEN = Object.freeze([]);
var EMPTY_DATASET = Object.freeze({});
function extractElement({
  node,
  nodeId,
  positionalData
}) {
  const attrs = [];
  if ("attributes" in node) {
    for (let i = 0; i < node.attributes.length; i++) {
      const attr = node.attributes.item(i);
      if (!attr) continue;
      attrs.push({
        name: attr.name,
        value: attr.value
      });
    }
  }
  return {
    attributes: attrs,
    textContent: null,
    tagName: node.tagName,
    dataset: node.dataset != null && Object.keys(node.dataset).length > 0 ? Object.assign({}, node.dataset) : EMPTY_DATASET,
    children: [],
    nodeId,
    nextNodeId: positionalData?.siblings.next ?? null,
    prevNodeId: positionalData?.siblings.prev ?? null
  };
}
function extractText({
  text,
  nodeId,
  positionalData
}) {
  return {
    tagName: "#text",
    textContent: text.textContent,
    attributes: EMPTY_FROZEN,
    dataset: EMPTY_DATASET,
    children: EMPTY_FROZEN,
    nodeId,
    nextNodeId: positionalData?.siblings.next ?? null,
    prevNodeId: positionalData?.siblings.prev ?? null
  };
}

// src/record/helpers/documentExtractor.ts
var DocumentExtractor = class {
  constructor(documentObserver) {
    this.pendingIframes = [];
    this.pendingShadowRoots = [];
    this.documentObserver = documentObserver;
  }
  evaluateIframes() {
    if (this.pendingIframes.length === 0) return;
    for (const iframe of this.pendingIframes) {
      this.documentObserver.engine.subFrameObserver.onIframeAdded(iframe);
    }
    this.pendingIframes = [];
  }
  evaluateShadowRoots() {
    if (this.pendingShadowRoots.length === 0) return;
    for (const shadowRootHost of this.pendingShadowRoots) {
      this.documentObserver.engine.subFrameObserver.onShadowRootAdded(
        shadowRootHost
      );
    }
    this.pendingShadowRoots = [];
  }
  evaluatePendingFrames() {
    this.evaluateIframes();
    this.evaluateShadowRoots();
  }
  extractNode({
    node,
    positionalData,
    segment,
    when
  }, recursiveOpts) {
    if (node.nodeType === node.COMMENT_NODE) return null;
    let data = null;
    const nodeId = this.documentObserver.getNodeId(node);
    if (nodeId == null) return null;
    let tagName = null;
    if (node.nodeType === Node.TEXT_NODE) {
      data = extractText({
        text: node,
        nodeId,
        positionalData
      });
      if (recursiveOpts?.parentTagName === "STYLE") {
        const cssText = this.documentObserver.stylesheetObserver.handleInlineStyleTextNode(
          node
        );
        if (cssText != null) {
          data.textContent = cssText;
        }
      }
    } else {
      try {
        const extracted = extractElement({
          node,
          nodeId,
          positionalData
        });
        data = extracted;
        tagName = node.tagName;
        this.documentObserver.engine.positionTracker.queueElement(
          nodeId,
          node,
          this.documentObserver.frameId
        );
        if (node.tagName === "IMG") {
          data.attributes.push({
            name: "width",
            value: node.width.toString()
          });
          data.attributes.push({
            name: "height",
            value: node.height.toString()
          });
          this.documentObserver.engine.patcher.patchInlineImage({
            img: node,
            nodeId,
            segment: segment ?? this.documentObserver.engine.getSegment(),
            when: when ?? Date.now()
          });
        }
        if (node.tagName === "use") {
          this.documentObserver.engine.patcher.patchSvgUseHref({
            useElement: node,
            nodeId,
            segment: segment ?? this.documentObserver.engine.getSegment(),
            when: when ?? Date.now()
          });
        }
        if (node.tagName === "IFRAME") {
          this.pendingIframes.push(node);
        } else if ("shadowRoot" in node && node.shadowRoot != null) {
          this.pendingShadowRoots.push(node);
        }
      } catch (e) {
        this.documentObserver.engine.error("Error extracting node", e);
      }
    }
    if (data && "childNodes" in node && node.childNodes.length > 0) {
      node.childNodes.forEach((child) => {
        const ext = this.extractNode(
          {
            node: child,
            when,
            segment
          },
          {
            parentTagName: tagName ?? void 0
          }
        );
        if (!ext) return;
        data.children.push(ext);
      });
    }
    return data;
  }
};

// src/record/stormDetector.ts
var MutationStormDetector = class {
  constructor(config) {
    this.windowStart = 0;
    this.windowCount = 0;
    this.stormStart = null;
    this.stormTotal = 0;
    this.debounceTimer = null;
    this.windowMs = config?.windowMs ?? 50;
    this.threshold = config?.threshold ?? 500;
    this.hardLimit = config?.hardLimit ?? 800;
    this.maxDurationMs = config?.maxDurationMs ?? 5e3;
    this.debounceMs = config?.debounceMs ?? 50;
  }
  /**
   * Record `count` incoming mutation records and return the action the
   * caller should take:
   * - `"normal"` — process mutations as usual
   * - `"debounce"` — hold processing and wait for the storm to settle
   * - `"snapshot"` — discard mutations and take a fresh full snapshot
   */
  record(count) {
    const now = Date.now();
    if (now - this.windowStart > this.windowMs) {
      this.windowStart = now;
      this.windowCount = 0;
    }
    this.windowCount += count;
    if (this.stormStart != null) {
      this.stormTotal += count;
      if (this.stormTotal >= this.hardLimit || now - this.stormStart > this.maxDurationMs) {
        this.exitStorm();
        return "snapshot";
      }
      return "debounce";
    }
    if (this.windowCount >= this.threshold) {
      this.stormStart = now;
      this.stormTotal = this.windowCount;
      return "debounce";
    }
    return "normal";
  }
  /**
   * Schedule a debounced callback. Each call resets the timer so the
   * callback only fires once the storm has settled.
   */
  scheduleDebounce(callback) {
    if (this.debounceTimer != null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      callback();
    }, this.debounceMs);
  }
  exitStorm() {
    this.stormStart = null;
    this.stormTotal = 0;
    this.windowCount = 0;
    if (this.debounceTimer != null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
  destroy() {
    this.exitStorm();
  }
};

// src/record/observers/index.ts
var Observer = class {
  constructor(engine, target) {
    this.ready = false;
    this.engine = engine;
    this.observeId = this.engine.getNewObserveId();
    this.target = target;
  }
  isReady() {
    this.ready = true;
  }
  destroy() {
    this.engine.onObserverDestroyed(this.observeId);
  }
};

// src/record/observers/scroll.ts
var ScrollObserver = class extends Observer {
  constructor(engine, target, documentObserver) {
    super(engine, target);
    this.tsBetweenScrolls = 25;
    this.lastScrollTs = null;
    this.touchedScrollElements = /* @__PURE__ */ new Set();
    this.lastEvalDebounceTimeout = null;
    this.boundOnScroll = null;
    this.documentObserver = documentObserver;
  }
  onScroll(evt) {
    const when = Date.now();
    if (this.lastScrollTs && when - this.lastScrollTs < this.tsBetweenScrolls)
      return;
    this.lastScrollTs = when;
    let scrolledElement = null;
    if ("scrollTop" in evt.target) {
      scrolledElement = evt.target;
    } else if ("scrollingElement" in evt.target) {
      scrolledElement = evt.target.scrollingElement;
    }
    if (!scrolledElement) return;
    if (!this.touchedScrollElements.has(scrolledElement)) {
      this.touchedScrollElements.add(scrolledElement);
    }
    this.scrolled(scrolledElement, when);
    if (this.lastEvalDebounceTimeout)
      clearTimeout(this.lastEvalDebounceTimeout);
    this.lastEvalDebounceTimeout = setTimeout(() => {
      this.lastEvalDebounceTimeout = null;
      this.evalCurrentScrollPositions();
    }, 50);
  }
  scrolled(scrolledElement, when) {
    const nodeId = this.documentObserver.getNodeId(scrolledElement);
    if (nodeId == null) return;
    this.documentObserver.frameAwareEmitEvent({
      type: "scroll",
      when: when ?? Date.now(),
      payload: {
        node: nodeId,
        scrollTop: scrolledElement.scrollTop,
        scrollLeft: scrolledElement.scrollLeft
      }
    });
  }
  evalCurrentScrollPositions() {
    for (const scrolledElement of this.touchedScrollElements) {
      this.scrolled(scrolledElement);
    }
    this.touchedScrollElements.clear();
  }
  setup() {
    this.boundOnScroll = this.onScroll.bind(this);
    this.target.addEventListener("scroll", this.boundOnScroll, {
      capture: true,
      passive: true
    });
  }
  full() {
  }
  destroy() {
    if (this.boundOnScroll) {
      this.target.removeEventListener("scroll", this.boundOnScroll, {
        capture: true
      });
    }
    this.touchedScrollElements.clear();
    if (this.lastEvalDebounceTimeout)
      clearTimeout(this.lastEvalDebounceTimeout);
    super.destroy();
  }
};

// src/record/observers/selection.ts
var SelectionObserver = class extends Observer {
  constructor(engine, target, documentObserver) {
    super(engine, target);
    this.debounceTimeout = null;
    this.debounceMs = 50;
    this.onSelectionChange = () => {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.debounceTimeout = null;
        this.emitSelection();
      }, this.debounceMs);
    };
    this.documentObserver = documentObserver;
  }
  emitSelection() {
    const selection = this.target.getSelection?.() ?? window.getSelection();
    if (!selection) return;
    if (selection.isCollapsed || !selection.anchorNode || !selection.focusNode) {
      this.documentObserver.frameAwareEmitEvent({
        type: "selection",
        payload: null,
        when: Date.now()
      });
      return;
    }
    const anchorNodeResult = this.documentObserver.engine.queryNodeId(selection.anchorNode);
    const focusNodeResult = this.documentObserver.engine.queryNodeId(selection.focusNode);
    if (!anchorNodeResult || !focusNodeResult) return;
    this.documentObserver.frameAwareEmitEvent({
      type: "selection",
      payload: {
        anchorNodeId: anchorNodeResult.nodeId,
        anchorOffset: selection.anchorOffset,
        focusNodeId: focusNodeResult.nodeId,
        focusOffset: selection.focusOffset
      },
      when: Date.now()
    });
  }
  setup() {
    this.target.addEventListener("selectionchange", this.onSelectionChange, {
      capture: true
    });
    this.isReady();
  }
  full() {
  }
  destroy() {
    this.target.removeEventListener("selectionchange", this.onSelectionChange, {
      capture: true
    });
    if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
    super.destroy();
  }
};

// src/record/cssUtils.ts
var processCssText = (cssText) => {
  const results = [];
  let i = 0;
  while (i < cssText.length) {
    while (i < cssText.length && /\s/.test(cssText[i])) i++;
    if (i >= cssText.length) break;
    const braceIdx = cssText.indexOf("{", i);
    if (braceIdx === -1) break;
    const name = cssText.slice(i, braceIdx).trim();
    let depth = 1;
    let j = braceIdx + 1;
    while (j < cssText.length && depth > 0) {
      if (cssText[j] === "{") depth++;
      else if (cssText[j] === "}") depth--;
      j++;
    }
    const blockContent = cssText.slice(braceIdx + 1, j - 1);
    i = j;
    if (blockContent.includes("{")) {
      if (name.startsWith("@media") || name.startsWith("@supports")) {
        results.push(...processCssText(blockContent));
      } else {
        results.push({
          name,
          properties: [],
          cssText: `${name}{${blockContent}}`
        });
      }
      continue;
    }
    const properties = blockContent.split(";").map((prop) => {
      const trimmed = prop.trim();
      if (!trimmed) return null;
      const colonIdx = trimmed.indexOf(":");
      if (colonIdx === -1) return null;
      return {
        name: trimmed.slice(0, colonIdx).trim(),
        value: trimmed.slice(colonIdx + 1).trim()
      };
    }).filter(Boolean);
    const ruleCssText = `${name}{${blockContent}}`;
    results.push({ name, properties, cssText: ruleCssText });
  }
  return results;
};
function stripEmptyDeclarations(cssText) {
  return cssText.replace(/[\w-]+\s*:\s*;/g, "");
}
function flattenCssRules(rules, win, originalRuleTexts) {
  const texts = [];
  let didIncludeMediaRule = false;
  try {
    for (const rule of rules) {
      const ruleName = rule.constructor.name;
      if (ruleName === "CSSMediaRule") {
        didIncludeMediaRule = true;
        if (win.matchMedia(rule.conditionText).matches) {
          try {
            const res = flattenCssRules(
              //@ts-expect-error
              Array.from(rule.cssRules),
              win,
              originalRuleTexts
            ).cssText;
            texts.push(res);
          } catch (e) {
          }
        }
      } else if (ruleName === "CSSSupportsRule") {
        if (CSS.supports(rule.conditionText)) {
          try {
            texts.push(
              flattenCssRules(
                //@ts-expect-error
                Array.from(rule.cssRules),
                win,
                originalRuleTexts
              ).cssText
            );
          } catch (e) {
          }
        }
      } else if (ruleName === "CSSLayerBlockRule") {
        try {
          const innerCss = flattenCssRules(
            //@ts-expect-error
            Array.from(rule.cssRules),
            win,
            originalRuleTexts
          ).cssText;
          if (innerCss) {
            texts.push(
              `@layer ${rule.name || ""} { ${innerCss} }`
            );
          }
        } catch (e) {
        }
      } else {
        const original = originalRuleTexts?.get(rule);
        texts.push(original ?? stripEmptyDeclarations(rule.cssText));
      }
    }
  } catch (e) {
  }
  return {
    cssText: texts.join(""),
    didIncludeMediaRule
  };
}
function repairCSSOMSerialisation(cssomText, rawText) {
  const expectedProperties = processCssText(rawText);
  const actualProperties = processCssText(cssomText);
  const matchedExpectedIndices = /* @__PURE__ */ new Set();
  let finalCssText = "";
  for (const actualProperty of actualProperties) {
    const expectedIndex = expectedProperties.findIndex(
      (prop, idx) => !matchedExpectedIndices.has(idx) && prop.name === actualProperty.name
    );
    if (expectedIndex === -1) {
      finalCssText += actualProperty.cssText;
      continue;
    }
    matchedExpectedIndices.add(expectedIndex);
    const expectedProperty = expectedProperties[expectedIndex];
    if (actualProperty.properties.length === 0 && expectedProperty.properties.length === 0) {
      const actualInner = extractBlockContent(actualProperty.cssText);
      const expectedInner = extractBlockContent(expectedProperty.cssText);
      if (actualInner != null && expectedInner != null) {
        const repairedInner = repairCSSOMSerialisation(
          actualInner,
          expectedInner
        );
        finalCssText += `${actualProperty.name}{${repairedInner}}`;
      } else {
        finalCssText += actualProperty.cssText;
      }
      continue;
    }
    let part = actualProperty.cssText;
    const insertPos = part.indexOf("{");
    if (insertPos === -1) {
      finalCssText += part;
      continue;
    }
    let toInsert = "";
    for (const expectedProp of expectedProperty.properties) {
      if (actualProperty.properties.find(
        (prop) => prop.name === expectedProp.name
      ))
        continue;
      toInsert += `${expectedProp.name}:${expectedProp.value};`;
    }
    if (toInsert) {
      part = part.slice(0, insertPos + 1) + toInsert + part.slice(insertPos + 1);
    }
    finalCssText += part;
  }
  return finalCssText;
}
function extractBlockContent(cssText) {
  const openBrace = cssText.indexOf("{");
  if (openBrace === -1) return null;
  const closeBrace = cssText.lastIndexOf("}");
  if (closeBrace <= openBrace) return null;
  return cssText.slice(openBrace + 1, closeBrace);
}
var processInlineStyleFromTextNode = (textNode, win) => {
  const style = textNode.parentElement;
  if (!style || style.tagName !== "STYLE")
    return {
      didIncludeMediaRule: false,
      cssText: null
    };
  const cssRules = style.sheet?.cssRules;
  if (!cssRules)
    return {
      didIncludeMediaRule: false,
      cssText: null
    };
  const { cssText, didIncludeMediaRule } = flattenCssRules(
    Array.from(cssRules),
    win,
    void 0
  );
  if (!textNode.textContent)
    return {
      cssText,
      didIncludeMediaRule
    };
  return {
    cssText: repairCSSOMSerialisation(cssText, textNode.textContent),
    didIncludeMediaRule,
    style
  };
};

// src/record/observers/stylesheet.ts
var STYLESHEET_MUTATION_DEBOUNCE = 100;
var StylesheetObserver = class extends Observer {
  constructor(engine, target, documentObserver) {
    super(engine, target);
    this.sheets = /* @__PURE__ */ new WeakMap();
    this.sheetsWithMediaQueries = /* @__PURE__ */ new Set();
    this.inlineStyleSheetsWithMediaQueries = /* @__PURE__ */ new Set();
    this.linkNodesObserved = /* @__PURE__ */ new WeakSet();
    /**
     * Maps CSSRule objects → the original CSS text supplied to insertRule().
     * CSSOM serialisation (`rule.cssText`) breaks shorthand properties that
     * use var() by expanding them into longhands with empty values.
     * Keeping the original text lets flattenCssRules emit correct CSS.
     */
    this.originalRuleTexts = /* @__PURE__ */ new WeakMap();
    this.viewportResizeSubscription = null;
    this.canFullRevaluateAfterMs = 5e3;
    // Tracks adopted (constructed) sheets so we can detect removals
    this.adoptedSheets = /* @__PURE__ */ new Map();
    this.originals = null;
    //the queue basically drops all mutations happening in a short period of time for the same stylesheet
    //a mutation is always the complete css so we can just drop all and take the last
    this.mutationsQueue = /* @__PURE__ */ new Map();
    this.isProcessingMutationsQueue = false;
    this.documentObserver = documentObserver;
  }
  canEvaluateFullSheet(sheet) {
    if (!this.sheets.has(sheet)) return true;
    const { updated } = this.sheets.get(sheet);
    if (Date.now() - updated < this.canFullRevaluateAfterMs) return false;
    return true;
  }
  get defaultView() {
    if ("defaultView" in this.target) return this.target.defaultView;
    return this.target.ownerDocument?.defaultView ?? window;
  }
  handleSheet(sheet, force, skipInline) {
    const when = Date.now();
    let ownerNode = null;
    if (sheet.ownerNode) {
      if (sheet.ownerNode.nodeType === Node.ELEMENT_NODE && sheet.ownerNode.nodeName === "STYLE") {
        if (sheet.ownerNode.textContent?.trim())
          return null;
      }
      const nodeId = this.documentObserver.getNodeId(sheet.ownerNode);
      if (nodeId) ownerNode = nodeId;
    }
    let rules = [];
    const existing = this.sheets.get(sheet);
    const sheetId = existing?.sheetId ?? this.engine.getNewSheetId();
    try {
      rules = Array.from(sheet.cssRules ?? sheet.rules ?? []);
    } catch (e) {
      if (!sheet.href) {
        this.engine.warn("error getting rules from sheet", sheet, e);
        return null;
      }
      const didCreate = existing == null;
      this.sheets.set(sheet, {
        cssTextLength: 0,
        rulesLength: 0,
        sheetId,
        updated: when,
        ownerNode,
        isCORS: true
      });
      this.engine.patcher.patchCORSStylesheet({
        sheet,
        when,
        observer: this,
        segment: this.engine.getSegment()
      });
      return {
        cssText: "",
        sheetId,
        ownerNode,
        didCreate
      };
    }
    const canEvaluateFull = this.canEvaluateFullSheet(sheet);
    if (!canEvaluateFull && existing && !force) {
      if (existing.rulesLength === rules.length) return null;
    }
    const cssTextLength = existing?.cssTextLength ?? 0;
    const { cssText: result, didIncludeMediaRule } = flattenCssRules(
      rules,
      this.defaultView,
      this.originalRuleTexts
    );
    const cssText = result.trim().replace(/\n/g, "");
    if (cssTextLength === cssText.length && !force) return null;
    if (didIncludeMediaRule) this.sheetsWithMediaQueries.add(sheet);
    this.sheets.set(sheet, {
      cssTextLength: cssText.length,
      rulesLength: rules.length,
      sheetId,
      updated: when,
      ownerNode,
      isCORS: false
    });
    if (!skipInline) {
      const baseUrl = sheet.href ?? this.target.baseURI;
      this.engine.patcher.patchInlineStylesheet({
        cssText,
        sheetId,
        ownerNode,
        baseUrl,
        segment: this.engine.getSegment(),
        when,
        href: sheet.href
      });
    }
    return {
      cssText,
      sheetId,
      ownerNode,
      didCreate: existing == null
    };
  }
  handleInlineStyleTextNode(textNode) {
    const { didIncludeMediaRule, cssText, style } = processInlineStyleFromTextNode(textNode, this.defaultView);
    if (didIncludeMediaRule) {
      const nodeId = this.documentObserver.getNodeId(style);
      if (!nodeId) return cssText;
      this.inlineStyleSheetsWithMediaQueries.add(nodeId);
    }
    return cssText;
  }
  onSheetRemoved(when, sheetId) {
    this.documentObserver.frameAwareEmitEvent({
      type: "stylesheet-removed",
      payload: { sheetId },
      when
    });
  }
  onNodesRemoved(nodeIds) {
    for (const nodeId of nodeIds) {
      if (this.inlineStyleSheetsWithMediaQueries.has(nodeId))
        this.inlineStyleSheetsWithMediaQueries.delete(nodeId);
    }
  }
  onViewportResize() {
    const when = Date.now();
    for (const sheet of this.sheetsWithMediaQueries) {
      this.onSheetMutation(sheet, when);
    }
    for (const nodeId of this.inlineStyleSheetsWithMediaQueries) {
      const node = this.documentObserver.getNodeById(nodeId);
      if (!node) {
        this.inlineStyleSheetsWithMediaQueries.delete(nodeId);
        continue;
      }
      this.documentObserver.reRenderNode(nodeId);
    }
  }
  processMutationsQueue() {
    if (this.mutationsQueue.size === 0) {
      this.isProcessingMutationsQueue = false;
      return;
    }
    setTimeout(() => {
      const sheet = this.mutationsQueue.keys().next().value;
      if (!sheet) return this.processMutationsQueue();
      const when = this.mutationsQueue.get(sheet);
      if (!when) return this.processMutationsQueue();
      this.processSheetMutation(sheet, when);
      this.mutationsQueue.delete(sheet);
      this.processMutationsQueue();
    }, STYLESHEET_MUTATION_DEBOUNCE);
  }
  addToMutationsQueue(sheet, when) {
    this.mutationsQueue.set(sheet, when);
    if (!this.isProcessingMutationsQueue) {
      this.isProcessingMutationsQueue = true;
      this.processMutationsQueue();
    }
  }
  onSheetMutation(sheet, _when) {
    const when = _when ?? Date.now();
    this.addToMutationsQueue(sheet, when);
  }
  processSheetMutation(sheet, when) {
    if (sheet.ownerNode) {
      const sheetDoc = sheet.ownerNode.ownerDocument;
      if (sheetDoc && sheetDoc !== this.target) {
        const correctDocObserver = this.engine.getDocumentObserverForDocument(sheetDoc);
        if (correctDocObserver && correctDocObserver.stylesheetObserver !== this) {
          correctDocObserver.stylesheetObserver.onSheetMutation(sheet, when);
          return;
        }
      }
    }
    const result = this.handleSheet(sheet);
    if (!result) return;
    this.documentObserver.frameAwareEmitEvent({
      type: "stylesheet-mutation",
      payload: {
        sheet: {
          cssText: result.cssText,
          sheetId: result.sheetId,
          ownerNode: result.ownerNode
        },
        didCreate: result.didCreate
      },
      when
    });
  }
  setup() {
    const self = this;
    const proto = this.defaultView?.CSSStyleSheet.prototype;
    if (proto) {
      const origInsertRule = proto.insertRule;
      const origDeleteRule = proto.deleteRule;
      const origReplace = proto.replace;
      const origReplaceSync = proto.replaceSync;
      proto.insertRule = function(...args) {
        const result = origInsertRule.apply(this, args);
        try {
          const rule = this.cssRules[result];
          if (rule) self.originalRuleTexts.set(rule, args[0]);
        } catch {
        }
        self.onSheetMutation(this);
        return result;
      };
      proto.deleteRule = function(...args) {
        origDeleteRule.apply(this, args);
        self.onSheetMutation(this);
      };
      proto.replace = async function(...args) {
        return origReplace.apply(this, args).then((sheet) => {
          self.onSheetMutation(sheet);
          return sheet;
        });
      };
      proto.replaceSync = function(...args) {
        origReplaceSync.apply(this, args);
        self.onSheetMutation(this);
      };
      const adoptedDesc = Object.getOwnPropertyDescriptor(
        this.defaultView?.Document.prototype,
        "adoptedStyleSheets"
      );
      if (adoptedDesc?.set) {
        const origSet = adoptedDesc.set;
        const origGet = adoptedDesc.get;
        Object.defineProperty(
          this.defaultView?.Document.prototype,
          "adoptedStyleSheets",
          {
            set(sheets) {
              origSet.call(this, sheets);
              self.onAdoptedSheetsChanged(sheets);
            },
            get() {
              return origGet?.call(this);
            },
            configurable: true
          }
        );
      }
      this.originals = {
        insertRule: origInsertRule,
        deleteRule: origDeleteRule,
        replace: origReplace,
        replaceSync: origReplaceSync,
        adoptedStyleSheetsDesc: adoptedDesc
      };
    }
    this.viewportResizeSubscription = this.engine.viewportObserver.sub(
      this.onViewportResize.bind(this)
    );
    this.isReady();
  }
  nativeReplaceSync(sheet, text) {
    if (this.originals) {
      this.originals.replaceSync.call(sheet, text);
    } else {
      sheet.replaceSync(text);
    }
  }
  onAdoptedSheetsChanged(newSheets) {
    const when = Date.now();
    const newSet = new Set(newSheets);
    for (const [sheet, sheetId] of this.adoptedSheets) {
      if (!newSet.has(sheet)) {
        this.adoptedSheets.delete(sheet);
        this.onSheetRemoved(when, sheetId);
      }
    }
    for (const sheet of newSheets) {
      if (!this.adoptedSheets.has(sheet)) {
        const sheetId = this.sheets.get(sheet)?.sheetId;
        if (sheetId != null) {
          this.adoptedSheets.set(sheet, sheetId);
        }
        const result = this.handleSheet(sheet);
        if (result) {
          if (result.didCreate) {
            this.adoptedSheets.set(sheet, result.sheetId);
          }
          this.documentObserver.frameAwareEmitEvent({
            type: "stylesheet-mutation",
            payload: {
              sheet: {
                cssText: result.cssText,
                sheetId: result.sheetId,
                ownerNode: result.ownerNode
              },
              didCreate: result.didCreate
            },
            when
          });
        }
      }
    }
  }
  onLinkLoad(link) {
    if (!link.sheet) return;
    this.onSheetMutation(link.sheet);
  }
  listenToLink(link) {
    if (this.linkNodesObserved.has(link)) return;
    this.linkNodesObserved.add(link);
    link.addEventListener(
      "load",
      () => {
        this.onLinkLoad(link);
      },
      { once: true }
    );
  }
  onNodesAdded(nodeIds) {
    for (const nodeId of nodeIds) {
      const node = this.documentObserver.getNodeById(nodeId);
      if (!node) continue;
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.tagName !== "LINK") continue;
      if ("rel" in node && node.rel === "stylesheet") {
        this.listenToLink(node);
      }
    }
  }
  full(args) {
    const when = args?.when ?? Date.now();
    this.sheetsWithMediaQueries = /* @__PURE__ */ new Set();
    this.inlineStyleSheetsWithMediaQueries = /* @__PURE__ */ new Set();
    const doc = this.documentObserver.target;
    const sheets = Array.from(doc.styleSheets);
    const sheetsPayload = { sheets: [] };
    for (const sheet of sheets) {
      const result = this.handleSheet(sheet, true);
      if (result) {
        sheetsPayload.sheets.push({
          cssText: result.cssText,
          sheetId: result.sheetId,
          ownerNode: result.ownerNode
        });
      }
    }
    const adopted = doc.adoptedStyleSheets;
    if (adopted && adopted.length > 0) {
      for (const sheet of adopted) {
        const result = this.handleSheet(sheet, true);
        if (result) {
          sheetsPayload.sheets.push({
            cssText: result.cssText,
            sheetId: result.sheetId,
            ownerNode: result.ownerNode
          });
        }
      }
    }
    doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      this.listenToLink(link);
    });
    if (sheetsPayload.sheets.length === 0 && !args?.initial) return;
    this.documentObserver.frameAwareEmitEvent({
      type: "stylesheet-snapshot",
      payload: sheetsPayload,
      when
    });
  }
  destroy() {
    this.engine.viewportObserver.unsub(this.viewportResizeSubscription);
    if (!this.originals) return;
    const proto = this.defaultView?.CSSStyleSheet.prototype;
    if (!proto) return;
    proto.insertRule = this.originals.insertRule;
    proto.deleteRule = this.originals.deleteRule;
    proto.replace = this.originals.replace;
    proto.replaceSync = this.originals.replaceSync;
    if (this.originals.adoptedStyleSheetsDesc) {
      Object.defineProperty(
        this.defaultView?.Document.prototype,
        "adoptedStyleSheets",
        this.originals.adoptedStyleSheetsDesc
      );
    }
    this.originals = null;
    this.adoptedSheets.clear();
    super.destroy();
  }
};

// src/record/observers/document.ts
var ALLOWED_NODE_TYPES = [Node.TEXT_NODE, Node.ELEMENT_NODE];
var DocumentObserver = class extends Observer {
  constructor() {
    super(...arguments);
    this.nodeMap = /* @__PURE__ */ new WeakMap();
    this.nodeIdToNodeMap = /* @__PURE__ */ new Map();
    this.lastFullSnapshot = null;
    this.minTimeBetweenSnapshots = 1e3;
    this.mutObservers = [];
    this.observers = [];
    this.extractor = new DocumentExtractor(this);
    this.stormDetector = new MutationStormDetector();
    this.mutationEventBuffer = new EventBuffer(
      this.processMutationBatch.bind(this)
    );
    this.frameId = null;
  }
  setFrameId(frameId) {
    this.frameId = frameId;
  }
  getNodeId(node) {
    if (!node) return void 0;
    return this.nodeMap.get(node);
  }
  getNodeById(nodeId) {
    return this.nodeIdToNodeMap.get(nodeId);
  }
  trackNode(element) {
    try {
      if (this.nodeMap.has(element)) {
        return [];
      }
      const nodeId = this.engine.getNewNodeId();
      const nodeIds = [nodeId];
      try {
        this.nodeMap.set(element, nodeId);
        this.nodeIdToNodeMap.set(nodeId, element);
      } catch (e) {
        this.engine.error(
          "Error setting node map (probably invalid weak key)",
          e
        );
        this.engine.error(element, nodeId);
      }
      if (element.childNodes.length > 0) {
        element.childNodes.forEach((child) => {
          nodeIds.push(...this.trackNode(child));
        });
      }
      return nodeIds;
    } catch (e) {
      this.engine.error("Error tracking node", e);
      return [];
    }
  }
  collectNodeIdsToUntrack(element, out) {
    const nodeId = this.getNodeId(element);
    if (nodeId == null) return;
    out.push(nodeId);
    for (let i = 0; i < element.childNodes.length; i++) {
      this.collectNodeIdsToUntrack(element.childNodes[i], out);
    }
  }
  removeTrackedNode(element) {
    if (!this.nodeMap.has(element)) return [];
    let nodeIds = [];
    this.collectNodeIdsToUntrack(element, nodeIds);
    if (nodeIds.length === 0) return [];
    this.onNodesRemoved(nodeIds);
    for (const nodeId of nodeIds) {
      const node = this.nodeIdToNodeMap.get(nodeId);
      if (node) this.nodeMap.delete(node);
      this.nodeIdToNodeMap.delete(nodeId);
    }
    return nodeIds;
  }
  /**
   * Runs synchronously in the MutationObserver callback. Only does cheap work:
   * node ID tracking (map updates), sibling positional reads, and saving node
   * references. The expensive extraction (extractNode, getBoundingClientRect,
   * attribute iteration) is deferred to processMutations.
   *
   * Sibling positional data must be captured here because previousSibling /
   * nextSibling become null once the node is detached from the DOM.
   */
  eagerProcessMutation(mutation) {
    const when = Date.now();
    const segment = this.engine.getSegment();
    if (mutation.target === this.target && this.frameId != null) {
      return {
        when,
        segment,
        isDocumentWrite: true,
        addedNodes: [],
        removedNodeIds: [],
        targetNodeId: 0,
        attributeName: null,
        targetElement: null,
        isCharacterData: false
      };
    }
    const addedNodes = [];
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
        const nodeIds = this.trackNode(node);
        if (nodeIds.length === 0) {
          return;
        }
        addedNodes.push({
          node,
          mainNodeId: nodeIds[0],
          positionalData: {
            siblings: {
              prev: this.getNodeId(node.previousSibling) ?? null,
              next: this.getNodeId(node.nextSibling) ?? null
            }
          }
        });
        this.onNodesAdded(nodeIds);
      }
    });
    const removedNodeIds = [];
    if (mutation.removedNodes.length > 0) {
      mutation.removedNodes.forEach((node) => {
        if (ALLOWED_NODE_TYPES.includes(node.nodeType))
          removedNodeIds.push(
            ...this.removeTrackedNode(node)
          );
      });
    }
    const targetElement = mutation.target;
    const targetNodeId = this.getNodeId(targetElement);
    if (targetNodeId == null) {
      return null;
    }
    return {
      when,
      segment,
      isDocumentWrite: false,
      addedNodes,
      removedNodeIds,
      targetNodeId,
      attributeName: mutation.attributeName,
      targetElement,
      isCharacterData: mutation.type === "characterData"
    };
  }
  /**
   * Handles the expensive work: node extraction, attribute reads, and event
   * emission. Called via requestIdleCallback with the full batch of eager
   * data collected from a MutationObserver callback (or multiple callbacks
   * that fired before idle ran).
   *
   * Merges all mutation records into a single MutationPayload so one
   * MutationObserver callback = one emitted event (instead of N).
   *
   * Node references saved by eagerProcessMutation may point to detached nodes
   * by the time this runs. Most DOM properties (tagName, attributes,
   * textContent, childNodes) are still readable on detached nodes.
   * getBoundingClientRect() returns zeros — an acceptable tradeoff for
   * allowing deferred processing.
   *
   * EDGE CASE — add-then-remove within the idle window:
   * If a node is added and then removed before this runs, removeTrackedNode
   * will have already deleted it from the nodeMap. extractNode will call
   * getNodeId(node) which returns undefined, so the add is silently skipped.
   * The remove event still emits with the node ID. Replay sees a remove for a
   * node that was never added — a benign no-op.
   */
  processMutationBatch(batch) {
    if (batch.length === 0) return;
    let didDocumentWrite = false;
    const bySegment = /* @__PURE__ */ new Map();
    for (const eagerData of batch) {
      if (eagerData.isDocumentWrite) {
        didDocumentWrite = true;
        continue;
      }
      let group = bySegment.get(eagerData.segment);
      if (!group) {
        group = { items: [], earliestWhen: eagerData.when };
        bySegment.set(eagerData.segment, group);
      }
      group.items.push(eagerData);
      if (eagerData.when < group.earliestWhen) {
        group.earliestWhen = eagerData.when;
      }
    }
    for (const [segment, group] of bySegment) {
      const mutationPayload = {};
      for (const eagerData of group.items) {
        for (const addedNode of eagerData.addedNodes) {
          const extracted = this.extractor.extractNode({
            node: addedNode.node,
            positionalData: addedNode.positionalData,
            segment: eagerData.segment,
            when: eagerData.when
          });
          if (!extracted) continue;
          if (!mutationPayload.adds) mutationPayload.adds = [];
          mutationPayload.adds.push({
            parentId: eagerData.targetNodeId,
            element: extracted
          });
        }
        if (eagerData.removedNodeIds.length > 0) {
          if (!mutationPayload.removes) mutationPayload.removes = [];
          mutationPayload.removes.push(...eagerData.removedNodeIds);
        }
        if (eagerData.attributeName && eagerData.targetElement) {
          if (!mutationPayload.attributes) mutationPayload.attributes = [];
          mutationPayload.attributes.push({
            nodeId: eagerData.targetNodeId,
            attributeName: eagerData.attributeName,
            value: eagerData.targetElement.getAttribute(
              eagerData.attributeName
            )
          });
        }
        if (eagerData.isCharacterData && eagerData.targetElement) {
          const extracted = this.extractor.extractNode({
            node: eagerData.targetElement,
            segment: eagerData.segment,
            when: eagerData.when
          });
          if (extracted) {
            if (!mutationPayload.replaces) mutationPayload.replaces = [];
            mutationPayload.replaces.push({
              nodeId: eagerData.targetNodeId,
              element: extracted
            });
          }
        }
      }
      const hasPayload = mutationPayload.adds || mutationPayload.removes || mutationPayload.attributes || mutationPayload.replaces;
      if (!hasPayload) continue;
      this.frameAwareEmitEvent({
        type: "mutation",
        payload: mutationPayload,
        segment,
        when: group.earliestWhen
      });
    }
    this.extractor.evaluatePendingFrames();
    if (didDocumentWrite) {
      this.full();
      this.stylesheetObserver.full();
    }
  }
  frameAwareEmitEvent(event) {
    if (this.frameId != null) event.frameId = this.frameId;
    this.engine.emitEvent(event);
  }
  reRenderNode(nodeId) {
    const when = Date.now();
    const node = this.getNodeById(nodeId);
    if (!node) return;
    const parentNodeId = this.getNodeId(node.parentNode);
    if (parentNodeId == null) return;
    const extracted = this.extractor.extractNode({
      node,
      segment: this.engine.getSegment(),
      when: Date.now()
    });
    if (!extracted) return;
    this.frameAwareEmitEvent({
      type: "mutation",
      payload: {
        replaces: [
          {
            nodeId,
            element: extracted
          }
        ]
      },
      when
    });
    this.extractor.evaluatePendingFrames();
  }
  onMutation(mutation) {
    const processed = this.eagerProcessMutation(mutation);
    if (!processed) return;
    this.mutationEventBuffer.add(processed);
  }
  get rootNode() {
    if ("documentElement" in this.target) return this.target.documentElement;
    return this.target;
  }
  setup() {
    this.trackNode(this.rootNode);
    const mutObserver = new MutationObserver((mutations) => {
      const action = this.stormDetector.record(mutations.length);
      if (action === "snapshot") {
        this.mutationEventBuffer.clear();
        this.engine.debug("Mutation storm: triggering full snapshot");
        this.full();
        this.stylesheetObserver.full();
        return;
      }
      if (action === "debounce") {
        this.mutationEventBuffer.hold();
      }
      for (const mut of mutations) {
        this.onMutation(mut);
      }
      if (action === "debounce") {
        this.stormDetector.scheduleDebounce(() => {
          this.stormDetector.exitStorm();
          this.mutationEventBuffer.release();
        });
      }
    });
    mutObserver.observe(this.target, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });
    this.mutObservers.push(mutObserver);
    this.observers.push(
      this.engine.addObserver(
        new ScrollObserver(this.engine, this.target, this)
      )
    );
    if (this.target.nodeType === Node.DOCUMENT_NODE) {
      this.observers.push(
        this.engine.addObserver(
          new SelectionObserver(this.engine, this.target, this)
        )
      );
    }
    this.stylesheetObserver = this.engine.addObserver(
      new StylesheetObserver(this.engine, this.target, this),
      this.frameId != null
    );
    this.observers.push(this.stylesheetObserver);
    this.isReady();
  }
  onNodesRemoved(nodeIds) {
    this.engine.subFrameObserver.onNodesRemoved(nodeIds);
    this.stylesheetObserver.onNodesRemoved(nodeIds);
  }
  onNodesAdded(nodeIds) {
    this.stylesheetObserver.onNodesAdded(nodeIds);
  }
  full(args) {
    const when = args?.when ?? Date.now();
    if (!args?.initial) {
      this.nodeIdToNodeMap.clear();
      this.nodeMap = /* @__PURE__ */ new WeakMap();
      this.trackNode(this.rootNode);
    }
    const data = this.extractor.extractNode({
      node: this.rootNode,
      when
    });
    if (!data) return;
    this.frameAwareEmitEvent({
      type: "snapshot",
      payload: {
        snapshotElement: data
      },
      when
    });
    this.extractor.evaluatePendingFrames();
    if (args?.initial && this.frameId != null) {
      this.stylesheetObserver.full(args);
    }
  }
  destroy() {
    this.stormDetector.destroy();
    this.mutationEventBuffer.destroy();
    this.nodeIdToNodeMap.clear();
    this.mutObservers.forEach((mutObserver) => {
      mutObserver.disconnect();
    });
    this.observers.forEach((observer) => {
      observer.destroy();
    });
    this.observers = [];
    this.mutObservers = [];
    super.destroy();
  }
};

// src/record/observers/interactions.ts
var ALLOWED_KEYS = ["control", "meta", "shift", "alt", "altgraph"];
var InteractionsObserver = class extends Observer {
  constructor() {
    super(...arguments);
    this.boundOnFocusChange = null;
    this.boundOnBlur = null;
    this.boundKeyUp = null;
    this.boundKeyDown = null;
    this.boundKeyPress = null;
  }
  onKeyUp(evt) {
    this.handleKeyEvent("keyup", evt);
  }
  onKeyDown(evt) {
    this.handleKeyEvent("keydown", evt);
  }
  onKeyPress(evt) {
    this.handleKeyEvent("keypress", evt);
  }
  handleKeyEvent(type, evt) {
    const when = Date.now();
    const key = evt.key.toLowerCase();
    const ctrl = evt.ctrlKey;
    const meta = evt.metaKey;
    const shift = evt.shiftKey;
    let nodeId = void 0;
    try {
      const target = evt.target;
      const nodeIdResult = this.engine.queryNodeId(target)?.nodeId;
      if (nodeIdResult != null) {
        nodeId = nodeIdResult;
      }
    } catch (err) {
    }
    this.engine.emitEvent({
      type: "keyboard",
      payload: {
        kind: type,
        ctrl,
        meta,
        shift,
        key: ALLOWED_KEYS.includes(key) ? key : void 0,
        nodeId
      },
      when
    });
  }
  onFocusChange(evt) {
    const when = Date.now();
    const target = evt.target;
    const nodeIdResult = this.engine.queryNodeId(target);
    if (nodeIdResult == null) return;
    this.engine.emitEvent({
      type: "focus-element",
      payload: {
        nodeId: nodeIdResult.nodeId,
        isFocused: target === document.activeElement
      },
      when
    });
  }
  onBlur(evt) {
    const when = Date.now();
    const target = evt.target;
    const nodeIdResult = this.engine.queryNodeId(target);
    if (nodeIdResult == null) return;
    this.engine.emitEvent({
      type: "focus-element",
      payload: {
        nodeId: nodeIdResult.nodeId,
        isFocused: false
      },
      when
    });
  }
  setup() {
    this.isReady();
    this.boundOnFocusChange = this.onFocusChange.bind(this);
    this.target.addEventListener("focus", this.boundOnFocusChange, {
      capture: true
    });
    this.boundOnBlur = this.onBlur.bind(this);
    this.target.addEventListener("blur", this.boundOnBlur, {
      capture: true
    });
    this.boundKeyUp = this.onKeyUp.bind(this);
    this.boundKeyDown = this.onKeyDown.bind(this);
    this.boundKeyPress = this.onKeyPress.bind(this);
    this.target.addEventListener("keyup", this.boundKeyUp, {
      capture: true
    });
    this.target.addEventListener("keydown", this.boundKeyDown, {
      capture: true
    });
    this.target.addEventListener("keypress", this.boundKeyPress, {
      capture: true
    });
  }
  full() {
  }
  destroy() {
    if (this.boundOnFocusChange)
      this.target.removeEventListener("focus", this.boundOnFocusChange);
    if (this.boundOnBlur)
      this.target.removeEventListener("blur", this.boundOnBlur);
    if (this.boundKeyUp)
      this.target.removeEventListener("keyup", this.boundKeyUp);
    if (this.boundKeyDown)
      this.target.removeEventListener("keydown", this.boundKeyDown);
    if (this.boundKeyPress)
      this.target.removeEventListener("keypress", this.boundKeyPress);
  }
};

// src/record/observers/mouse.ts
var MouseObserver = class extends Observer {
  constructor() {
    super(...arguments);
    this.tsBetweenMouseMovements = 50;
    this.lastMouseMovement = null;
    this.boundOnClick = null;
    this.boundOnMouseMove = null;
  }
  //keeping this pass-through method for now.
  //we might want to do some processing/batching here later.
  processMouseEvent(payload, when) {
    this.engine.emitEvent({
      type: "mouse",
      payload,
      when,
      segment: payload.segment
    });
  }
  onClick(evt) {
    try {
      const when = Date.now();
      const target = evt.target;
      const nodeIdResult = this.engine.queryNodeId(target);
      if (nodeIdResult == null) return;
      this.engine.emitEvent({
        type: "mouse-click",
        payload: {
          nodeId: nodeIdResult.nodeId,
          x: evt.clientX,
          y: evt.clientY
        },
        when
      });
    } catch (err) {
    }
  }
  onMouseMove(evt) {
    const when = Date.now();
    if (this.lastMouseMovement && when - this.lastMouseMovement < this.tsBetweenMouseMovements)
      return;
    this.lastMouseMovement = when;
    this.processMouseEvent(
      {
        x: evt.clientX,
        y: evt.clientY,
        segment: this.engine.getSegment()
      },
      when
    );
  }
  setup() {
    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.target.addEventListener("mousemove", this.boundOnMouseMove, {
      passive: true
    });
    this.boundOnClick = this.onClick.bind(this);
    this.target.addEventListener("click", this.boundOnClick, {
      capture: true
    });
    this.isReady();
  }
  full() {
  }
  destroy() {
    if (this.boundOnMouseMove)
      this.target.removeEventListener("mousemove", this.boundOnMouseMove);
    if (this.boundOnClick)
      this.target.removeEventListener("click", this.boundOnClick);
    super.destroy();
  }
};

// src/record/observers/subframe.ts
var SubFrameObserver = class extends Observer {
  constructor() {
    super(...arguments);
    this.subDocumentMap = /* @__PURE__ */ new Map();
    this.elementsChanged = /* @__PURE__ */ new Set();
  }
  setup() {
    const self = this;
    const origAttachShadow = Element.prototype.attachShadow;
    Element.prototype.attachShadow = function(init) {
      const shadowRoot = origAttachShadow.call(this, init);
      this.__axosShadowRoot = shadowRoot;
      self.observeSubFrame(this, "shadow");
      return shadowRoot;
    };
    this._origAttachShadow = origAttachShadow;
    this.isReady();
  }
  onIframeAdded(iframe) {
    this.observeSubFrame(iframe, "iframe");
  }
  onShadowRootAdded(shadowRoot) {
    this.observeSubFrame(shadowRoot, "shadow");
  }
  onNodesRemoved(nodeIds) {
    for (const nodeId of nodeIds) {
      const result = this.engine.queryGetNodeById(nodeId);
      if (!result) continue;
      if (result.node instanceof HTMLIFrameElement) {
        this.onIframeRemoved(result.node);
      }
    }
  }
  detach(iframe, observer) {
    observer.destroy();
    this.subDocumentMap.delete(iframe);
  }
  onIframeRemoved(iframe) {
    const docObserver = this.subDocumentMap.get(iframe);
    if (!docObserver) return;
    this.detach(iframe, docObserver);
  }
  observeSubFrame(element, type) {
    if (this.elementsChanged.has(element)) return;
    this.elementsChanged.add(element);
    if (this.subDocumentMap.has(element)) return;
    const tryObserve = () => {
      let root;
      if (type === "iframe") {
        try {
          const doc = element.contentDocument;
          if (!doc?.documentElement) return false;
          root = doc;
        } catch {
          return false;
        }
      } else {
        const sr = element.shadowRoot ?? element.__axosShadowRoot;
        if (!sr) return false;
        root = sr;
      }
      const existing = this.subDocumentMap.get(element);
      if (existing && existing.target === root) return true;
      if (existing) this.detach(element, existing);
      const result = this.engine.queryNodeId(element);
      if (!result || result.nodeId == null) return false;
      const observer = new DocumentObserver(this.engine, root);
      observer.setFrameId(result.nodeId);
      this.engine.addObserver(observer);
      this.subDocumentMap.set(element, observer);
      return true;
    };
    if (type === "iframe") {
      element.addEventListener("load", () => tryObserve(), { once: true });
    }
    tryObserve();
  }
  full() {
    this.target.querySelectorAll("iframe").forEach((iframe) => {
      this.observeSubFrame(iframe, "iframe");
    });
    const walker = this.target.createTreeWalker(
      this.target.documentElement,
      NodeFilter.SHOW_ELEMENT
    );
    while (walker.nextNode()) {
      const el = walker.currentNode;
      if (el.shadowRoot || el.__axosShadowRoot) {
        this.observeSubFrame(el, "shadow");
      }
    }
  }
  reset() {
    for (const [, docObserver] of this.subDocumentMap) {
      docObserver.destroy();
    }
    this.subDocumentMap.clear();
    this.elementsChanged.clear();
  }
  destroy() {
    this.reset();
    super.destroy();
    if (this._origAttachShadow) Element.prototype.attachShadow = this._origAttachShadow;
  }
  teardown() {
    this.reset();
  }
};

// src/record/observers/viewport.ts
var ViewportObserver = class extends Observer {
  constructor() {
    super(...arguments);
    this.onResize = null;
    this.resizeTimeout = null;
    this.currentSize = null;
    this.resizeObserver = null;
    this.subscribers = /* @__PURE__ */ new Map();
    this.subscriberId = 0;
    this.pollTimeout = null;
  }
  sub(callback) {
    const id = this.subscriberId++;
    this.subscribers.set(id, callback);
    return id;
  }
  unsub(id) {
    if (id == null) return;
    this.subscribers.delete(id);
  }
  notify(size) {
    for (const callback of this.subscribers.values()) callback(size);
  }
  resize(force = false) {
    const size = {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight
    };
    if (force !== true && this.currentSize && this.currentSize.width === size.width && this.currentSize.height === size.height)
      return;
    this.currentSize = size;
    if (force) {
      this.notify(size);
      return this.emit({
        when: Date.now(),
        ...size
      });
    }
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.notify(size);
      this.emit({
        when: Date.now(),
        ...size
      });
    }, 125);
  }
  emit(args) {
    this.engine.emitEvent({
      type: "meta",
      payload: {
        width: args.width,
        height: args.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      },
      when: args.when
    });
  }
  setup() {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.pollResize();
    });
    this.resizeObserver.observe(this.target.document.documentElement);
    this.onResize = () => {
      this.resize();
    };
    this.target.addEventListener("resize", this.onResize);
    this.isReady();
  }
  pollResize(times = 0) {
    if (this.pollTimeout) clearTimeout(this.pollTimeout);
    if (times > 2) return;
    this.pollTimeout = setTimeout(() => {
      this.resize();
      this.pollResize(times + 1);
    }, 500);
  }
  full() {
    this.resize(true);
    this.pollResize();
  }
  destroy() {
    if (this.onResize) this.target.removeEventListener("resize", this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.pollTimeout) clearTimeout(this.pollTimeout);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.subscribers.clear();
    super.destroy();
  }
};

// src/record/persistent.ts
function fnv1a(str) {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
}
var PersistentStore = class {
  constructor(recordEngine) {
    this.patchedSheetsHashes = /* @__PURE__ */ new Set();
    this.patchedSheetsHashByHref = /* @__PURE__ */ new Map();
    this.urlHashMapping = /* @__PURE__ */ new Map();
    this.recordEngine = recordEngine;
  }
  getUrlHash(url) {
    return this.urlHashMapping.get(url);
  }
  storeUrlWithHash({
    url,
    raw,
    result,
    when,
    segment
  }) {
    if (this.urlHashMapping.has(url)) return this.urlHashMapping.get(url);
    const hash = fnv1a(result);
    this.urlHashMapping.set(url, hash);
    this.recordEngine.emitEvent({
      type: "store-stylesheet-inline",
      payload: {
        hashes: [
          {
            hash,
            url,
            result,
            raw
          }
        ]
      },
      when,
      segment
    });
    return hash;
  }
  storeStylesheetPatch({
    cssText,
    when,
    segment,
    href
  }) {
    const hash = fnv1a(cssText);
    if (href) this.patchedSheetsHashByHref.set(href, hash);
    if (this.patchedSheetsHashes.has(hash)) return hash;
    this.patchedSheetsHashes.add(hash);
    this.recordEngine.emitEvent({
      type: "store-stylesheet",
      payload: {
        hash,
        cssText
      },
      when,
      segment
    });
    return hash;
  }
  getStylesheetHashByHref(href) {
    return this.patchedSheetsHashByHref.get(href);
  }
  destroy() {
    this.patchedSheetsHashByHref.clear();
    this.patchedSheetsHashes.clear();
    this.urlHashMapping.clear();
  }
};

// src/record/patcher.ts
var URL_RE = /url\(\s*["']?([^"')]+)["']?\s*\)/g;
var Patcher = class {
  constructor(recordEngine) {
    this._canvas = null;
    this.svgFetchCache = /* @__PURE__ */ new Map();
    this.patchedSvgFragments = /* @__PURE__ */ new Set();
    this.recordEngine = recordEngine;
    this.persistentStore = new PersistentStore(recordEngine);
  }
  get canvas() {
    if (!this._canvas) this._canvas = document.createElement("canvas");
    return this._canvas;
  }
  cleanupCanvas() {
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
  patchNode({
    nodeId,
    element,
    segment,
    when
  }) {
    this.recordEngine.emitEvent({
      type: "node-patch",
      payload: {
        nodeId,
        element
      },
      when,
      segment
    });
  }
  patchInlineImage({
    img,
    nodeId,
    segment,
    when
  }) {
    const imageSrc = (img.currentSrc ?? img.getAttribute("src") ?? "<unknown-src>") + "";
    if (imageSrc.includes("data:image")) return;
    const canvas = this.canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let overrideImage = null;
    const onError = (e) => {
      overrideImage = null;
    };
    const drawImage = () => {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL();
        this.cleanupCanvas();
        this.patchNode({
          nodeId,
          element: {
            attributes: [
              {
                name: "src",
                value: dataUrl
              }
            ]
          },
          segment,
          when
        });
      } catch (e) {
        if (overrideImage) {
          onError();
          return;
        }
        overrideImage = new Image();
        overrideImage.src = imageSrc;
        overrideImage.crossOrigin = "anonymous";
        overrideImage.height = img.naturalHeight;
        overrideImage.width = img.naturalWidth;
        if (overrideImage.complete && overrideImage.naturalWidth !== 0) {
          runIdleCallback(drawImage);
        } else {
          overrideImage.addEventListener(
            "load",
            () => runIdleCallback(drawImage),
            {
              once: true
            }
          );
          overrideImage.addEventListener("error", onError, {
            once: true
          });
        }
      }
    };
    if (img.complete && img.naturalWidth > 0) {
      runIdleCallback(drawImage);
    } else {
      img.addEventListener("load", () => runIdleCallback(drawImage), {
        once: true
      });
      img.addEventListener("error", onError, { once: true });
    }
  }
  patchSvgUseHref({
    useElement,
    nodeId,
    segment,
    when
  }) {
    const href = useElement.getAttribute("href") ?? useElement.getAttributeNS("http://www.w3.org/1999/xlink", "href");
    if (!href) return;
    if (href.startsWith("data:") || href.startsWith("blob:") || href.startsWith("#"))
      return;
    let resolvedUrl;
    try {
      resolvedUrl = new URL(href, document.baseURI);
    } catch {
      return;
    }
    const fragment = resolvedUrl.hash;
    const fragmentId = fragment.slice(1);
    resolvedUrl.hash = "";
    const fetchUrl = resolvedUrl.href;
    const parentSvg = useElement.closest("svg");
    if (!parentSvg) return;
    const parentResult = this.recordEngine.queryNodeId(parentSvg);
    if (!parentResult) return;
    const parentSvgNodeId = parentResult.nodeId;
    const hrefAttrName = useElement.hasAttribute("href") ? "href" : "xlink:href";
    if (!this.svgFetchCache.has(fetchUrl)) {
      const promise = fetch(fetchUrl).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      }).then((svgText) => {
        const parser = new DOMParser();
        return parser.parseFromString(svgText, "image/svg+xml");
      }).catch(() => null).finally(() => {
        this.svgFetchCache.delete(fetchUrl);
      });
      this.svgFetchCache.set(fetchUrl, promise);
    }
    this.svgFetchCache.get(fetchUrl).then((doc) => {
      if (!doc) return;
      const referencedElement = fragmentId ? doc.getElementById(fragmentId) : doc.documentElement;
      if (!referencedElement) return;
      const patchKey = `${parentSvgNodeId}:${fetchUrl}${fragment}`;
      if (!this.patchedSvgFragments.has(patchKey)) {
        this.patchedSvgFragments.add(patchKey);
        const extractedRef = this.extractDomTree(referencedElement);
        if (!extractedRef) return;
        const defsElement = {
          tagName: "defs",
          attributes: [],
          textContent: null,
          dataset: {},
          children: [extractedRef],
          nodeId: 0,
          nextNodeId: null,
          prevNodeId: null
        };
        this.patchNode({
          nodeId: parentSvgNodeId,
          element: {
            children: [defsElement]
          },
          segment,
          when
        });
      }
      this.patchNode({
        nodeId,
        element: {
          attributes: [
            {
              name: hrefAttrName,
              value: fragment || "#" + referencedElement.id
            }
          ]
        },
        segment,
        when
      });
    }).catch(() => {
    });
  }
  extractDomTree(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return extractText({ text: node, nodeId: 0 });
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const data = extractElement({ node, nodeId: 0 });
    for (const child of Array.from(node.childNodes)) {
      const extracted = this.extractDomTree(child);
      if (extracted) data.children.push(extracted);
    }
    return data;
  }
  async patchStylesheet({
    cssText,
    href,
    when,
    segment,
    sheetId,
    ownerNode
  }) {
    const inlinedCssText = await this.handleInlineCssUrls({
      cssText,
      baseUrl: href,
      when,
      segment,
      fullText: true
    });
    if (Array.isArray(inlinedCssText)) return;
    const hash = this.persistentStore.storeStylesheetPatch({
      cssText: inlinedCssText,
      href,
      when,
      segment
    });
    this.recordEngine.debug("patched stylesheet", href);
    this.recordEngine.emitEvent({
      type: "stylesheet-patch",
      payload: {
        sheet: {
          hash,
          sheetId,
          ownerNode
        }
      },
      when,
      segment
    });
  }
  useCachedHrefStylesheet({
    href,
    when,
    segment,
    sheetId,
    ownerNode
  }) {
    const hrefHash = this.persistentStore.getStylesheetHashByHref(href);
    if (hrefHash == null) return false;
    this.recordEngine.debug("using cached stylesheet", href);
    this.recordEngine.emitEvent({
      type: "stylesheet-patch",
      payload: {
        sheet: {
          hash: hrefHash,
          sheetId,
          ownerNode
        }
      },
      when,
      segment
    });
    return true;
  }
  patchCORSStylesheet({
    sheet,
    segment,
    when,
    observer
  }) {
    if (!sheet.href) return;
    const href = sheet.href;
    const meta = observer.sheets.get(sheet);
    if (!meta) return;
    const usedCached = this.useCachedHrefStylesheet({
      href,
      when,
      segment,
      sheetId: meta.sheetId,
      ownerNode: meta.ownerNode
    });
    if (usedCached) return;
    this.recordEngine.debug("patching cors stylesheet", href);
    fetch(href).then(async (response) => {
      const resText = await response.text();
      const newSheet = new CSSStyleSheet();
      observer.nativeReplaceSync(newSheet, resText);
      const result = observer.handleSheet(newSheet, true, true);
      if (!result) return;
      this.patchStylesheet({
        cssText: result.cssText,
        href,
        when,
        segment,
        sheetId: meta.sheetId,
        ownerNode: meta.ownerNode
      });
    }).catch((e) => {
      this.recordEngine.error(
        "error fetching href stylesheet",
        sheet.href,
        e
      );
    });
  }
  /**
   * Scans cssText for `url()` references (fonts, images, etc.), fetches
   * each resource, converts it to a base64 data-URI and emits a
   * stylesheet-patch with the fully-inlined CSS.  This makes replay
   * self-contained — it no longer depends on external resources that may
   * have changed or disappeared.
   */
  async patchInlineStylesheet({
    cssText,
    sheetId,
    ownerNode,
    baseUrl,
    segment,
    href,
    when
  }) {
    if (href) {
      const didHandle = await this.handleCSSOMSerialization({
        cssText,
        segment,
        when,
        href,
        sheetId,
        ownerNode
      });
      if (didHandle) return;
    }
    if (!/url\(\s*["']?(?!["']?\s*\))(?!data:|blob:)/i.test(cssText)) return;
    const hashes = await this.handleInlineCssUrls({
      baseUrl,
      cssText,
      segment,
      when
    });
    if (!Array.isArray(hashes)) return;
    if (hashes.length === 0) return;
    this.recordEngine.emitEvent({
      type: "stylesheet-patch",
      payload: {
        hashes,
        sheetId
      },
      when,
      segment
    });
  }
  async handleCSSOMSerialization({
    cssText,
    segment,
    when,
    href,
    sheetId,
    ownerNode
  }) {
    const usedCached = this.useCachedHrefStylesheet({
      href,
      when,
      segment,
      sheetId,
      ownerNode
    });
    if (usedCached) return true;
    const response = await fetch(href);
    if (!response.ok) return false;
    const resText = await response.text();
    const newCssText = repairCSSOMSerialisation(cssText, resText);
    if (newCssText.length === cssText.length) return false;
    this.patchStylesheet({
      cssText: newCssText,
      href,
      when,
      segment,
      sheetId,
      ownerNode
    });
    return true;
  }
  async handleInlineCssUrls({
    cssText,
    baseUrl,
    when,
    segment,
    fullText
  }) {
    const urlsToFetch = /* @__PURE__ */ new Map();
    let m;
    const hashes = /* @__PURE__ */ new Set();
    while ((m = URL_RE.exec(cssText)) !== null) {
      const raw = m[1].trim();
      if (raw.startsWith("data:") || raw.startsWith("blob:")) continue;
      try {
        const resolved = new URL(raw, baseUrl).href;
        const currentHash = this.persistentStore.getUrlHash(resolved);
        if (currentHash != null) {
          hashes.add(currentHash);
          continue;
        }
        urlsToFetch.set(resolved, raw);
      } catch {
      }
    }
    const uriMapping = {};
    if (urlsToFetch.size > 0) {
      await Promise.all(
        Array.from(urlsToFetch).map(async ([url, raw]) => {
          try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const dataUri = await this.blobToDataUri(blob);
            uriMapping[url] = dataUri;
            hashes.add(
              this.persistentStore.storeUrlWithHash({
                url,
                raw,
                result: dataUri,
                segment,
                when
              })
            );
          } catch {
          }
        })
      );
      if (!fullText) return Array.from(hashes);
    } else {
      if (!fullText) return Array.from(hashes);
      return cssText;
    }
    return cssText.replace(URL_RE, (full, raw) => {
      raw = raw.trim();
      if (raw.startsWith("data:") || raw.startsWith("blob:")) return full;
      try {
        const resolved = new URL(raw, baseUrl).href;
        const cached = uriMapping[resolved];
        if (cached) return `url("${cached}")`;
      } catch {
      }
      return full;
    });
  }
  blobToDataUri(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  destroy() {
    this.persistentStore.destroy();
    this.svgFetchCache.clear();
    this.patchedSvgFragments.clear();
    if (this._canvas) this._canvas.remove();
  }
};

// src/record/positionTracker.ts
var PositionTracker = class {
  constructor(engine) {
    this.queue = [];
    this.rafId = null;
    this.elementsPerFrame = 200;
    this.engine = engine;
  }
  queueElement(nodeId, element, frameId) {
    if (!("getBoundingClientRect" in element)) return;
    this.queue.push({ nodeId, element, frameId });
    if (this.rafId == null) this.scheduleFrame();
  }
  clear() {
    this.queue = [];
  }
  scheduleFrame() {
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.processChunk();
    });
  }
  processChunk() {
    if (this.queue.length === 0) return;
    const chunk = this.queue.splice(0, this.elementsPerFrame);
    const when = Date.now();
    const grouped = /* @__PURE__ */ new Map();
    for (const { nodeId, element, frameId } of chunk) {
      if (!element.isConnected) continue;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;
      let bucket = grouped.get(frameId);
      if (!bucket) {
        bucket = [];
        grouped.set(frameId, bucket);
      }
      bucket.push({
        nodeId,
        x: rect.x,
        y: rect.y,
        w: rect.width,
        h: rect.height
      });
    }
    for (const [frameId, positions] of grouped) {
      if (positions.length === 0) continue;
      const event = {
        type: "positions",
        payload: { positions },
        when
      };
      if (frameId != null) event.frameId = frameId;
      this.engine.emitEvent(event);
    }
    if (this.queue.length > 0) this.scheduleFrame();
  }
  destroy() {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.queue = [];
  }
};

// src/record/engine.ts
var RecordEngine = class {
  constructor(opts) {
    this.observers = [];
    this.plugins = [];
    this.consumerBuffer = new ConsumerBuffer(this);
    this.events = new EngineEvents(this);
    this.isDebug = false;
    this.isRunning = false;
    this.segmentIndex = 0;
    this.patcher = new Patcher(this);
    this.positionTracker = new PositionTracker(this);
    this._nodeId = 0;
    this._sheetId = 0;
    this._observeId = 0;
    this.snapshotEveryMsTimeout = null;
    this.isDebug = opts?.debug ?? false;
    this.consumerBuffer.consumeEvents = opts?.onEvents ?? null;
    if (opts?.store != null) {
      this.debug("Restoring from store", opts?.store);
      this.segmentIndex = (opts?.store?.lastSegment ?? -1) + 1;
    }
    if (opts?.plugins) {
      for (const plugin of opts.plugins) {
        this.addPlugin(plugin);
      }
    }
    this.viewportObserver = this.addObserver(
      new ViewportObserver(this, window)
    );
    this.mouseObserver = this.addObserver(new MouseObserver(this, window));
    this.addObserver(new InteractionsObserver(this, document));
    this.subFrameObserver = this.addObserver(
      new SubFrameObserver(this, document),
      true
    );
    this.addObserver(new DocumentObserver(this, document));
    this.subFrameObserver.full();
    this.isRunning = true;
    this.consumerBuffer.start();
    this.snapshotEveryMs = opts?.snapshotEveryMs;
    this.snapshotTimer();
  }
  getStateStore() {
    return {
      lastSegment: this.segmentIndex
    };
  }
  queryNodeId(node) {
    for (const observer of this.observers) {
      if (!(observer instanceof DocumentObserver)) continue;
      const result = observer.getNodeId(node);
      if (result != null)
        return {
          observer,
          nodeId: result
        };
    }
    return null;
  }
  queryGetNodeById(nodeId) {
    for (const observer of this.observers) {
      if (!(observer instanceof DocumentObserver)) continue;
      const result = observer.getNodeById(nodeId);
      if (result != null)
        return {
          observer,
          node: result
        };
    }
    return null;
  }
  getSegment() {
    return this.segmentIndex;
  }
  getNewNodeId() {
    if (this._nodeId >= Number.MAX_SAFE_INTEGER) this._nodeId = 0;
    return this._nodeId++;
  }
  getNewSheetId() {
    if (this._sheetId >= Number.MAX_SAFE_INTEGER) this._sheetId = 0;
    return this._sheetId++;
  }
  getNewObserveId() {
    if (this._observeId >= Number.MAX_SAFE_INTEGER) this._observeId = 0;
    return this._observeId++;
  }
  addPlugin(plugin) {
    this.plugins.push(plugin);
    plugin.setup?.(this);
  }
  removePlugin(name) {
    const idx = this.plugins.findIndex((p) => p.name === name);
    if (idx === -1) return;
    const [removed] = this.plugins.splice(idx, 1);
    removed.destroy?.();
  }
  /** Called by ConsumerBuffer after each batch is delivered. */
  notifyPlugins(events) {
    for (const plugin of this.plugins) {
      try {
        plugin.onEvents(events);
      } catch (e) {
        this.error(`[axos] Plugin "${plugin.name}" error in onEvents`, e);
      }
    }
  }
  full() {
    this.segmentIndex++;
    const when = Date.now();
    this.positionTracker.clear();
    this.subFrameObserver.teardown();
    this.observers.forEach((observer) => {
      if (observer.observeId === this.subFrameObserver.observeId) return;
      observer.full({
        when
      });
    });
    this.subFrameObserver.full();
    for (const plugin of this.plugins) {
      try {
        plugin.onSnapshot?.();
      } catch (e) {
        this.error(`[axos] Plugin "${plugin.name}" error in onSnapshot`, e);
      }
    }
  }
  addObserver(observer, skipFull) {
    this.observers.push(observer);
    observer.setup();
    if (skipFull === true) return observer;
    observer.full({ initial: true });
    return observer;
  }
  getDocumentObserverForDocument(doc) {
    for (const observer of this.observers) {
      if (observer instanceof DocumentObserver && observer.target === doc) {
        return observer;
      }
    }
    return void 0;
  }
  emitEvent(event) {
    const wrapped = event;
    wrapped.eventId = randomString();
    wrapped.segment = wrapped.segment ?? this.getSegment();
    this.consumerBuffer.onEvent(wrapped);
  }
  destroy() {
    this.isRunning = false;
    this.consumerBuffer.destroy();
    this.observers.forEach((observer) => {
      observer.destroy();
    });
    this.observers = [];
    for (const plugin of this.plugins) {
      try {
        plugin.destroy?.();
      } catch (e) {
        this.error(`[axos] Plugin "${plugin.name}" error in destroy`, e);
      }
    }
    this.plugins = [];
    if (this.snapshotEveryMsTimeout) clearTimeout(this.snapshotEveryMsTimeout);
    this.positionTracker.destroy();
    this.patcher.destroy();
    this.events.destroy();
  }
  onObserverDestroyed(observeId) {
    const observerIndex = this.observers.findIndex(
      (observer) => observer.observeId === observeId
    );
    if (observerIndex === -1) return;
    this.observers.splice(observerIndex, 1);
  }
  snapshotTimer() {
    if (this.snapshotEveryMs == null) return;
    this.snapshotEveryMsTimeout = setTimeout(() => {
      this.full();
      this.snapshotTimer();
    }, this.snapshotEveryMs);
  }
  debug(...args) {
    if (!this.isDebug) return;
    console.debug("[axos] Engine:", ...args);
  }
  error(...args) {
    if (!this.isDebug) return;
    console.error("[axos] Engine:", ...args);
  }
  warn(...args) {
    if (!this.isDebug) return;
    console.warn("[axos] Engine:", ...args);
  }
};
export {
  RecordEngine
};
